import { useState } from "react";

interface Props {
  onChoose: (path: "starter" | "builder" | "master" | "pro") => void;
  onSignIn?: () => void;
}

const TIERS = [
  {
    id:       "starter" as const,
    name:     "Money Starter",
    emoji:    "⭐",
    ages:     "Ages 10–12",
    desc:     "Build your money foundation — earning, saving, and why starting early wins.",
    bg:       "linear-gradient(135deg, #22c55e 0%, #06b6d4 100%)",
    shadow:   "rgba(34,197,94,0.3)",
    btnColor: "#16a34a",
  },
  {
    id:       "builder" as const,
    name:     "Money Builder",
    emoji:    "🚀",
    ages:     "Ages 13–15",
    desc:     "Stock market, index funds, and using credit without falling into debt traps.",
    bg:       "linear-gradient(135deg, #f97316 0%, #f59e0b 100%)",
    shadow:   "rgba(249,115,22,0.3)",
    btnColor: "#ea580c",
  },
  {
    id:       "master" as const,
    name:     "Money Master",
    emoji:    "🏆",
    ages:     "Ages 16–18",
    desc:     "Bonds, Roth IRAs, taxes, and real tools adults use every day.",
    bg:       "linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)",
    shadow:   "rgba(99,102,241,0.3)",
    btnColor: "#4f46e5",
  },
];

const STATS = [
  { n: "180+", label: "Lessons",   emoji: "📚" },
  { n: "100+", label: "Scenarios", emoji: "🎭" },
  { n: "Daily", label: "Streaks",   emoji: "🔥" },
  { n: "AI",   label: "MoneyBot",  emoji: "🤖" },
];

const FEATURES = [
  {
    emoji: "🗺️",
    title: "World progression",
    desc:  "Unlock 10+ worlds as your skills grow. Each world tackles a real money topic.",
    color: "#f97316",
  },
  {
    emoji: "🔥",
    title: "Daily streaks",
    desc:  "Earn XP, keep your streak alive, and watch your financial knowledge compound daily.",
    color: "#22c55e",
  },
  {
    emoji: "🎭",
    title: "Real scenarios",
    desc:  "100+ real-life money dilemmas. Make choices. See what happens. Learn for life.",
    color: "#6366f1",
  },
  {
    emoji: "🤖",
    title: "AI MoneyBot",
    desc:  "Ask any money question and get smart, age-appropriate answers powered by AI.",
    color: "#0d9488",
  },
];

const KIDS_HIGHLIGHTS = [
  { emoji: "🗺️", text: "10+ worlds — from Money Town to Global Investing" },
  { emoji: "🎭", text: "100+ real-life money scenarios" },
  { emoji: "🔥", text: "Daily streaks + XP rewards" },
  { emoji: "🐿️", text: "Stash the Squirrel guides your journey" },
];

const KIDS_OUTCOMES = [
  "Know why a $200 credit card mistake can turn into $600 — before they ever get a card",
  "Understand compound interest well enough to know $100 invested at 16 beats $300 invested at 26",
  "Tell a stock from a bond from an index fund — before opening a real brokerage account",
  "Build a budget that survives a real paycheck, not a worksheet",
];

const PRO_OUTCOMES = [
  "Know the right order to invest: 401k match → Roth IRA → everything else",
  "Build an emergency fund sized to their actual risk, not a generic \"3–6 months\" rule",
  "Tell good debt from bad debt before it becomes a $5,700 problem",
  "Track net worth instead of just a checking account balance",
];

const PRO_HIGHLIGHTS = [
  { emoji: "📊", text: "Budget tracker with category breakdown" },
  { emoji: "🎯", text: "Personalized plan: Conservative / Balanced / Aggressive" },
  { emoji: "📈", text: "Net worth goals + savings milestones" },
  { emoji: "🤖", text: "AI MoneyBot — real financial Q&A" },
  { emoji: "📚", text: "Pro curriculum: taxes, Roth IRAs, debt payoff" },
];

const PARENT_HIGHLIGHTS = [
  { emoji: "📊", title: "See what they learned",  desc: "Lesson-by-lesson progress, world completion, and weekly activity — all in one view." },
  { emoji: "🔐", title: "PIN-protected",           desc: "Your parent view is locked behind a 4-digit PIN. Kids stay in their lane." },
  { emoji: "💵", title: "Allowance tracker",       desc: "Set a weekly, biweekly, or monthly allowance. Get reminders when it's due." },
  { emoji: "🧹", title: "Chore management",        desc: "Create chores, assign coin rewards, and approve completions from your dashboard." },
];

const WHY_ITEMS = [
  {
    emoji: "📉",
    stat:  "Only 22%",
    text:  "of high school students take a personal finance course before graduating.",
    color: "#f97316",
  },
  {
    emoji: "💳",
    stat:  "$5,700",
    text:  "Average credit card debt for Americans under 35. MoneyMission teaches you to avoid it.",
    color: "#ef4444",
  },
  {
    emoji: "⏰",
    stat:  "Age 22 vs 32",
    text:  "Starting to invest at 22 instead of 32 can mean 3× more at retirement. Every year counts.",
    color: "#22c55e",
  },
];

export default function LandingScreen({ onChoose, onSignIn }: Props) {
  const [step, setStep] = useState<"marketing" | "tier">("marketing");
  const [leaving, setLeaving] = useState(false);

  function goToTier() {
    setLeaving(true);
    setTimeout(() => { setLeaving(false); setStep("tier"); }, 200);
  }

  function goBack() {
    setLeaving(true);
    setTimeout(() => { setLeaving(false); setStep("marketing"); }, 200);
  }

  const fade = {
    opacity:   leaving ? 0 : 1,
    transition: "opacity 0.2s",
  };

  /* ── TIER SELECTION ── */
  if (step === "tier") {
    return (
      <div style={{
        maxWidth: 480, margin: "0 auto", minHeight: "100vh",
        background: "linear-gradient(160deg, #f0fdfa 0%, #ecfdf5 100%)",
        padding: "28px 18px 48px",
      }}>
        <button
          onClick={goBack}
          style={{
            background: "rgba(255,255,255,0.8)",
            border: "none", borderRadius: 20, padding: "8px 14px",
            fontSize: 13, fontWeight: 800, color: "#64748b", cursor: "pointer",
            display: "inline-flex", alignItems: "center", gap: 6, marginBottom: 24,
            boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
          }}
        >
          ← Back
        </button>

        <div style={fade}>
          <div style={{ textAlign: "center", marginBottom: 28 }}>
            <div style={{ fontSize: 42, marginBottom: 8 }}>🗺️</div>
            <div style={{
              fontFamily: "'Fredoka One', cursive", fontSize: 28,
              color: "#1e293b", lineHeight: 1.1, marginBottom: 8,
            }}>
              Pick your starting level
            </div>
            <div style={{ fontSize: 13, color: "#64748b", fontWeight: 600, lineHeight: 1.5 }}>
              Don't worry — you can switch anytime.
            </div>
          </div>

          <div>
            {TIERS.map((tier, i) => (
              <button
                key={tier.id}
                onClick={() => onChoose(tier.id)}
                className="btn-bounce"
                style={{
                  display: "block", width: "100%", background: tier.bg,
                  border: "none", borderRadius: 22,
                  padding: "20px 20px 16px", cursor: "pointer", textAlign: "left",
                  boxShadow: `0 6px 24px ${tier.shadow}`,
                  animation: `slideUp 0.35s ease ${0.07 * i}s both`,
                  marginBottom: i < TIERS.length - 1 ? 14 : 0,
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 10 }}>
                  <div style={{ fontSize: 36, lineHeight: 1, flexShrink: 0 }}>{tier.emoji}</div>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                      <span style={{ fontFamily: "'Fredoka One', cursive", fontSize: 20, color: "#fff", lineHeight: 1 }}>
                        {tier.name}
                      </span>
                      <span style={{
                        background: "rgba(255,255,255,0.25)", borderRadius: 20,
                        padding: "2px 9px", fontSize: 10, fontWeight: 800, color: "#fff",
                      }}>
                        {tier.ages}
                      </span>
                    </div>
                    <div style={{ fontSize: 12, color: "rgba(255,255,255,0.9)", fontWeight: 600, marginTop: 4, lineHeight: 1.45 }}>
                      {tier.desc}
                    </div>
                  </div>
                </div>
                <div style={{
                  background: "#fff", color: tier.btnColor,
                  borderRadius: 40, padding: "7px 16px",
                  fontSize: 12, fontWeight: 900, display: "inline-block",
                }}>
                  Start here →
                </div>
              </button>
            ))}
          </div>

          <div style={{ marginTop: 32, textAlign: "center" }}>
            <div style={{ display: "flex", justifyContent: "center", gap: 20, flexWrap: "wrap" }}>
              <a
                href="https://moneymission.app/privacy"
                target="_blank"
                rel="noopener noreferrer"
                style={{ fontSize: 11, fontWeight: 700, color: "#64748b", textDecoration: "none" }}
              >
                Privacy Policy
              </a>
              <span style={{ fontSize: 11, color: "#cbd5e1" }}>·</span>
              <a
                href="https://moneymission.app/terms"
                target="_blank"
                rel="noopener noreferrer"
                style={{ fontSize: 11, fontWeight: 700, color: "#64748b", textDecoration: "none" }}
              >
                Terms of Service
              </a>
            </div>
          </div>
        </div>
      </div>
    );
  }

  /* ── MARKETING LANDING PAGE ── */
  return (
    <div style={{ maxWidth: 480, margin: "0 auto", minHeight: "100vh", display: "flex", flexDirection: "column" }}>

      {/* ── HERO ── */}
      <div style={{
        padding: "52px 24px 40px", textAlign: "center",
        background: "linear-gradient(160deg, #042f2e 0%, #0f4c41 60%, #134e4a 100%)",
        position: "relative", overflow: "hidden",
      }}>
        <div style={{ position: "absolute", top: -30, right: -30, width: 180, height: 180, borderRadius: "50%", background: "rgba(20,184,166,0.08)", pointerEvents: "none" }} />
        <div style={{ position: "absolute", bottom: -50, left: -30, width: 220, height: 220, borderRadius: "50%", background: "rgba(245,158,11,0.06)", pointerEvents: "none" }} />

        {/* Sign-in button — top right */}
        {onSignIn && (
          <button
            onClick={onSignIn}
            style={{
              position: "absolute", top: 14, right: 14,
              background: "rgba(255,255,255,0.1)",
              border: "1px solid rgba(255,255,255,0.22)",
              borderRadius: 20, padding: "7px 16px",
              fontSize: 12, fontWeight: 800, color: "rgba(255,255,255,0.85)",
              cursor: "pointer", zIndex: 2,
            }}
          >
            Sign in
          </button>
        )}

        <div style={{ position: "relative", animation: "pop 0.5s ease both" }}>

          {/* Logo badge — target (mission) + coin (money) */}
          <div style={{ position: "relative", display: "inline-block", marginBottom: 16 }}>
            <div style={{
              width: 80, height: 80, borderRadius: 22,
              background: "linear-gradient(135deg, #14b8a6 0%, #0f766e 100%)",
              display: "flex", alignItems: "center", justifyContent: "center",
              boxShadow: "0 8px 32px rgba(20,184,166,0.5)",
            }}>
              <svg width="46" height="46" viewBox="0 0 64 64" fill="none" aria-hidden="true">
                <circle cx="32" cy="32" r="26" stroke="#ffffff" strokeOpacity="0.85" strokeWidth="4" />
                <circle cx="32" cy="32" r="17" stroke="#fbbf24" strokeWidth="4" />
                <circle cx="32" cy="32" r="7" fill="#fbbf24" />
                <path d="M10 54 L27 37" stroke="#ffffff" strokeWidth="5" strokeLinecap="round" />
                <path d="M27 37 L24 45" stroke="#ffffff" strokeWidth="4" strokeLinecap="round" />
                <path d="M27 37 L19 40" stroke="#ffffff" strokeWidth="4" strokeLinecap="round" />
                <path d="M10 54 L9 61" stroke="#ffffff" strokeWidth="4" strokeLinecap="round" />
                <path d="M10 54 L3 55" stroke="#ffffff" strokeWidth="4" strokeLinecap="round" />
              </svg>
            </div>
            <div style={{
              position: "absolute", bottom: -6, right: -6,
              width: 30, height: 30, borderRadius: "50%",
              background: "linear-gradient(135deg, #f59e0b, #d97706)",
              display: "flex", alignItems: "center", justifyContent: "center",
              fontSize: 14, border: "2.5px solid #042f2e",
              boxShadow: "0 2px 8px rgba(245,158,11,0.5)",
            }}>🪙</div>
          </div>

          <div style={{
            fontFamily: "'Poppins', system-ui, sans-serif",
            fontWeight: 900, fontSize: 42,
            color: "#fff", lineHeight: 1, letterSpacing: "-1px", marginBottom: 10,
          }}>
            MoneyMission
          </div>
          <div style={{
            fontSize: 17, fontWeight: 800, marginBottom: 24, letterSpacing: 0.2,
            background: "linear-gradient(135deg, #14b8a6, #f59e0b)",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
          }}>
            Your money. Your mission.
          </div>

          <div style={{ marginBottom: 6 }}>
            <span style={{ fontSize: 26, color: "#fff", fontWeight: 800, lineHeight: 1.3 }}>
              The money skills
            </span>
          </div>
          <div style={{
            fontSize: 26, fontWeight: 800, lineHeight: 1.3, marginBottom: 24,
            background: "linear-gradient(135deg, #14b8a6, #f59e0b)",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
          }}>
            school never taught you.
          </div>

          <div style={{ display: "flex", gap: 8, justifyContent: "center", flexWrap: "wrap", marginBottom: 32 }}>
            {["Ages 10–25", "Free for kids"].map(tag => (
              <span key={tag} style={{
                background: "rgba(255,255,255,0.1)", borderRadius: 20,
                padding: "5px 12px", fontSize: 11, fontWeight: 700, color: "rgba(255,255,255,0.7)",
              }}>{tag}</span>
            ))}
          </div>

          <div style={{ fontSize: 12, color: "rgba(255,255,255,0.3)", fontWeight: 700, letterSpacing: 1.2 }}>
            ↓  SCROLL TO EXPLORE
          </div>
        </div>
      </div>

      {/* ── STATS BAR ── */}
      <div style={{
        display: "flex", background: "#fff",
        borderBottom: "1px solid #f1f5f9",
        boxShadow: "0 2px 12px rgba(0,0,0,0.04)",
      }}>
        {STATS.map((s, i) => (
          <div key={s.label} style={{
            flex: 1, textAlign: "center", padding: "14px 4px",
            borderRight: i < STATS.length - 1 ? "1px solid #f1f5f9" : "none",
          }}>
            <div style={{ fontSize: 18, marginBottom: 2 }}>{s.emoji}</div>
            <div style={{ fontSize: 16, fontWeight: 900, color: "#1e293b", lineHeight: 1 }}>{s.n}</div>
            <div style={{ fontSize: 9, fontWeight: 800, color: "#94a3b8", letterSpacing: 0.5, marginTop: 2 }}>
              {s.label.toUpperCase()}
            </div>
          </div>
        ))}
      </div>

      {/* ── FEATURES GRID ── */}
      <div style={{ padding: "32px 20px 28px", background: "#fff" }}>
        <div style={{ textAlign: "center", marginBottom: 24 }}>
          <div style={{ fontSize: 11, fontWeight: 800, color: "#94a3b8", letterSpacing: 1.5, marginBottom: 8 }}>
            WHAT'S INSIDE
          </div>
          <div style={{ fontFamily: "'Fredoka One', cursive", fontSize: 26, color: "#1e293b", lineHeight: 1.2 }}>
            Everything to master money
          </div>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          {FEATURES.map(f => (
            <div key={f.title} style={{
              background: "#f8fafc", borderRadius: 18, padding: "18px 14px",
              border: "1.5px solid #f1f5f9",
            }}>
              <div style={{
                width: 44, height: 44, borderRadius: 14,
                background: `${f.color}18`,
                display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: 22, marginBottom: 10,
              }}>
                {f.emoji}
              </div>
              <div style={{ fontSize: 13, fontWeight: 900, color: "#1e293b", marginBottom: 5, lineHeight: 1.2 }}>
                {f.title}
              </div>
              <div style={{ fontSize: 11, color: "#64748b", fontWeight: 600, lineHeight: 1.55 }}>
                {f.desc}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* ── FOR KIDS SECTION ── */}
      <div style={{ padding: "8px 20px 28px", background: "#fff" }}>
        <div style={{
          background: "linear-gradient(135deg, #f97316 0%, #ec4899 100%)",
          borderRadius: 24, padding: "24px 20px",
          boxShadow: "0 8px 32px rgba(249,115,22,0.2)",
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 12 }}>
            <div style={{ fontSize: 40, lineHeight: 1, flexShrink: 0 }}>🎮</div>
            <div>
              <div style={{ fontSize: 10, fontWeight: 800, color: "rgba(255,255,255,0.65)", letterSpacing: 1.5, marginBottom: 2 }}>
                MONEY ADVENTURE
              </div>
              <div style={{ fontFamily: "'Fredoka One', cursive", fontSize: 22, color: "#fff", lineHeight: 1.1 }}>
                For kids & teens
              </div>
            </div>
          </div>
          <div style={{ fontSize: 13, color: "rgba(255,255,255,0.92)", fontWeight: 600, lineHeight: 1.65, marginBottom: 16 }}>
            Money skills that stick — learned through adventures, games, and real-world choices. Not textbooks.
          </div>

          <div style={{ fontSize: 10, fontWeight: 800, color: "rgba(255,255,255,0.65)", letterSpacing: 1.2, marginBottom: 8 }}>
            BY THE END, THEY'LL:
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 18 }}>
            {KIDS_OUTCOMES.map(text => (
              <div key={text} style={{
                background: "rgba(255,255,255,0.14)", borderRadius: 12,
                padding: "9px 12px", display: "flex", alignItems: "flex-start", gap: 8,
              }}>
                <span style={{ fontSize: 12, flexShrink: 0, marginTop: 1 }}>✅</span>
                <span style={{ fontSize: 12, color: "#fff", fontWeight: 700, lineHeight: 1.5 }}>
                  {text}
                </span>
              </div>
            ))}
          </div>

          <div style={{ fontSize: 10, fontWeight: 800, color: "rgba(255,255,255,0.65)", letterSpacing: 1.2, marginBottom: 8 }}>
            HOW THEY GET THERE:
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 9 }}>
            {KIDS_HIGHLIGHTS.map(item => (
              <div key={item.text} style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
                <span style={{ fontSize: 14, flexShrink: 0, marginTop: 1 }}>{item.emoji}</span>
                <span style={{ fontSize: 12, color: "rgba(255,255,255,0.92)", fontWeight: 600, lineHeight: 1.5 }}>
                  {item.text}
                </span>
              </div>
            ))}
          </div>
          <div style={{ marginTop: 16, fontSize: 10, fontWeight: 800, color: "rgba(255,255,255,0.5)", letterSpacing: 1 }}>
            AGES 10–18
          </div>
        </div>
      </div>

      {/* ── FOR PARENTS SECTION ── */}
      <div style={{ padding: "8px 20px 28px", background: "#fff" }}>
        <div style={{
          background: "linear-gradient(135deg, #f0fdf4 0%, #ecfdf5 100%)",
          borderRadius: 24, padding: "24px 20px",
          border: "2px solid #bbf7d0",
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16 }}>
            <div style={{
              width: 52, height: 52, borderRadius: 16, flexShrink: 0,
              background: "linear-gradient(135deg, #10b981, #059669)",
              display: "flex", alignItems: "center", justifyContent: "center",
              fontSize: 26, boxShadow: "0 4px 14px rgba(16,185,129,0.3)",
            }}>👨‍👩‍👧</div>
            <div>
              <div style={{ fontSize: 10, fontWeight: 800, color: "#059669", letterSpacing: 1.5, marginBottom: 2 }}>
                FOR PARENTS
              </div>
              <div style={{ fontFamily: "'Fredoka One', cursive", fontSize: 22, color: "#064e3b", lineHeight: 1.1 }}>
                The most expensive thing you can skip
              </div>
            </div>
          </div>

          <div style={{ fontSize: 13, color: "#065f46", fontWeight: 600, lineHeight: 1.65, marginBottom: 18 }}>
            Only 1 in 5 high schoolers ever takes a personal finance class. The ones who don't learn it now usually learn it later — at 19, with a credit card, or at 25, wondering why there's nothing saved. MoneyMission is the class school skipped. A PIN-protected dashboard lets you watch it actually sink in: see what they're learning, manage their allowance, and approve chores — without snooping.
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
            {PARENT_HIGHLIGHTS.map(item => (
              <div key={item.title} style={{
                background: "#fff", borderRadius: 16, padding: "14px 12px",
                border: "1.5px solid #d1fae5",
              }}>
                <div style={{ fontSize: 22, marginBottom: 7 }}>{item.emoji}</div>
                <div style={{ fontSize: 12, fontWeight: 900, color: "#064e3b", marginBottom: 4, lineHeight: 1.2 }}>
                  {item.title}
                </div>
                <div style={{ fontSize: 10, color: "#6b7280", fontWeight: 600, lineHeight: 1.5 }}>
                  {item.desc}
                </div>
              </div>
            ))}
          </div>

          <div style={{
            marginTop: 16, background: "#dcfce7", borderRadius: 12,
            padding: "10px 14px", display: "flex", alignItems: "center", gap: 10,
          }}>
            <span style={{ fontSize: 16 }}>✅</span>
            <span style={{ fontSize: 12, fontWeight: 700, color: "#064e3b", lineHeight: 1.5 }}>
              Parent view is free with every account. No extra subscription needed.
            </span>
          </div>
        </div>
      </div>

      {/* ── FOR YOUNG ADULTS SECTION ── */}
      <div style={{ padding: "0 20px 28px", background: "#fff" }}>
        <div style={{
          background: "linear-gradient(135deg, #0f172a 0%, #1e3a5f 100%)",
          borderRadius: 24, padding: "24px 20px",
          border: "1.5px solid rgba(245,158,11,0.2)",
          boxShadow: "0 8px 32px rgba(245,158,11,0.08)",
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 12 }}>
            <div style={{ fontSize: 40, lineHeight: 1, flexShrink: 0 }}>💼</div>
            <div>
              <div style={{ fontSize: 10, fontWeight: 800, color: "rgba(245,158,11,0.8)", letterSpacing: 1.5, marginBottom: 2 }}>
                PRO MODE
              </div>
              <div style={{ fontFamily: "'Fredoka One', cursive", fontSize: 22, color: "#fff", lineHeight: 1.1 }}>
                For young adults
              </div>
            </div>
          </div>
          <div style={{ fontSize: 13, color: "#94a3b8", fontWeight: 600, lineHeight: 1.65, marginBottom: 16 }}>
            Stop guessing. Build a real financial plan — budget tracker, investment goals, and AI-powered guidance in one place.
          </div>

          <div style={{ fontSize: 10, fontWeight: 800, color: "rgba(245,158,11,0.8)", letterSpacing: 1.2, marginBottom: 8 }}>
            BY THE END, YOU'LL:
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 18 }}>
            {PRO_OUTCOMES.map(text => (
              <div key={text} style={{
                background: "rgba(245,158,11,0.1)", borderRadius: 12,
                padding: "9px 12px", display: "flex", alignItems: "flex-start", gap: 8,
                border: "1px solid rgba(245,158,11,0.18)",
              }}>
                <span style={{ fontSize: 12, flexShrink: 0, marginTop: 1 }}>✅</span>
                <span style={{ fontSize: 12, color: "#fff", fontWeight: 700, lineHeight: 1.5 }}>
                  {text}
                </span>
              </div>
            ))}
          </div>

          <div style={{ fontSize: 10, fontWeight: 800, color: "rgba(245,158,11,0.8)", letterSpacing: 1.2, marginBottom: 8 }}>
            HOW YOU GET THERE:
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 9 }}>
            {PRO_HIGHLIGHTS.map(item => (
              <div key={item.text} style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
                <span style={{ fontSize: 14, flexShrink: 0, marginTop: 1 }}>{item.emoji}</span>
                <span style={{ fontSize: 12, color: "#cbd5e1", fontWeight: 600, lineHeight: 1.5 }}>
                  {item.text}
                </span>
              </div>
            ))}
          </div>
          <div style={{ marginTop: 16, fontSize: 10, fontWeight: 800, color: "rgba(245,158,11,0.6)", letterSpacing: 1 }}>
            AGES 18+
          </div>
        </div>
      </div>

      {/* ── WHY IT MATTERS ── */}
      <div style={{ padding: "28px 20px", background: "#f8fafc" }}>
        <div style={{ textAlign: "center", marginBottom: 20 }}>
          <div style={{ fontSize: 11, fontWeight: 800, color: "#94a3b8", letterSpacing: 1.5, marginBottom: 8 }}>
            WHY IT MATTERS
          </div>
          <div style={{ fontFamily: "'Fredoka One', cursive", fontSize: 24, color: "#1e293b", lineHeight: 1.2, marginBottom: 6 }}>
            The financial literacy gap is real
          </div>
          <div style={{ fontSize: 12, color: "#64748b", fontWeight: 600 }}>
            This is exactly what MoneyMission is built to close.
          </div>
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {WHY_ITEMS.map(item => (
            <div key={item.stat} style={{
              background: "#fff", borderRadius: 16, padding: "16px",
              borderLeft: `4px solid ${item.color}`,
              display: "flex", alignItems: "flex-start", gap: 14,
            }}>
              <div style={{ fontSize: 28, lineHeight: 1, flexShrink: 0, marginTop: 2 }}>{item.emoji}</div>
              <div>
                <div style={{ fontSize: 16, fontWeight: 900, color: "#1e293b", marginBottom: 4 }}>
                  {item.stat}
                </div>
                <div style={{ fontSize: 12, color: "#475569", fontWeight: 600, lineHeight: 1.6 }}>
                  {item.text}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* ── PATH SELECTION CTA ── */}
      <div style={{ padding: "32px 20px 52px", background: "linear-gradient(160deg, #f0fdfa 0%, #ecfdf5 100%)" }}>
        <div style={{ textAlign: "center", marginBottom: 24 }}>
          <div style={{ fontSize: 11, fontWeight: 800, color: "#94a3b8", letterSpacing: 1.5, marginBottom: 8 }}>
            GET STARTED — FREE
          </div>
          <div style={{ fontFamily: "'Fredoka One', cursive", fontSize: 28, color: "#1e293b", lineHeight: 1.2 }}>
            Which path is yours?
          </div>
        </div>

        {/* Kids path */}
        <button
          onClick={goToTier}
          className="btn-bounce"
          style={{
            width: "100%", marginBottom: 14,
            background: "linear-gradient(135deg, #f97316 0%, #ec4899 100%)",
            border: "none", borderRadius: 24,
            padding: "24px 22px 20px", cursor: "pointer", textAlign: "left",
            boxShadow: "0 8px 32px rgba(249,115,22,0.3)",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 16, marginBottom: 12 }}>
            <div style={{ fontSize: 48, lineHeight: 1, flexShrink: 0 }}>🎮</div>
            <div>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4, flexWrap: "wrap" }}>
                <span style={{ fontFamily: "'Fredoka One', cursive", fontSize: 24, color: "#fff", lineHeight: 1 }}>
                  Money Adventure
                </span>
                <span style={{
                  background: "rgba(255,255,255,0.25)", borderRadius: 20,
                  padding: "2px 10px", fontSize: 10, fontWeight: 800, color: "#fff",
                }}>
                  Ages 10–18
                </span>
              </div>
              <div style={{ fontSize: 13, color: "rgba(255,255,255,0.92)", fontWeight: 600, lineHeight: 1.5 }}>
                Learn money through stories, games, and daily challenges. Build real skills — without it feeling like homework.
              </div>
            </div>
          </div>
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 14 }}>
            {["🗺️ World map", "🔥 Daily streak", "🎮 Mini-games", "🏆 Earn rewards", "👨‍👩‍👧 Parent dashboard"].map(f => (
              <span key={f} style={{
                background: "rgba(255,255,255,0.2)", borderRadius: 20,
                padding: "3px 10px", fontSize: 10, fontWeight: 700, color: "#fff",
              }}>{f}</span>
            ))}
          </div>
          <div style={{
            background: "#fff", color: "#ea580c",
            borderRadius: 40, padding: "10px 20px",
            fontSize: 14, fontWeight: 900, display: "inline-block",
          }}>
            Start Adventure →
          </div>
        </button>

        {/* Pro path */}
        <button
          onClick={() => onChoose("pro")}
          className="btn-bounce"
          style={{
            width: "100%",
            background: "linear-gradient(135deg, #0f172a 0%, #1e3a5f 100%)",
            border: "2px solid rgba(245,158,11,0.35)",
            borderRadius: 24,
            padding: "24px 22px 20px", cursor: "pointer", textAlign: "left",
            boxShadow: "0 8px 32px rgba(245,158,11,0.12)",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 16, marginBottom: 12 }}>
            <div style={{ fontSize: 48, lineHeight: 1, flexShrink: 0 }}>💼</div>
            <div>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4, flexWrap: "wrap" }}>
                <span style={{ fontFamily: "'Fredoka One', cursive", fontSize: 24, color: "#fff", lineHeight: 1 }}>
                  Pro Mode
                </span>
                <span style={{
                  background: "rgba(245,158,11,0.25)", borderRadius: 20,
                  padding: "2px 10px", fontSize: 10, fontWeight: 800, color: "#fbbf24",
                }}>
                  Ages 18+
                </span>
              </div>
              <div style={{ fontSize: 13, color: "#94a3b8", fontWeight: 600, lineHeight: 1.5 }}>
                Build a real financial plan. Track spending, set investment goals, and learn the tools young adults actually need.
              </div>
            </div>
          </div>
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 14 }}>
            {["📊 Budget tracker", "🎯 My Plan", "📈 Investments", "🤖 MoneyBot"].map(f => (
              <span key={f} style={{
                background: "rgba(245,158,11,0.15)", borderRadius: 20,
                padding: "3px 10px", fontSize: 10, fontWeight: 700, color: "#fbbf24",
              }}>{f}</span>
            ))}
          </div>
          <div style={{
            background: "linear-gradient(135deg, #f59e0b, #d97706)",
            color: "#fff",
            borderRadius: 40, padding: "10px 20px",
            fontSize: 14, fontWeight: 900, display: "inline-block",
          }}>
            Start Pro Mode →
          </div>
        </button>

        {/* Returning user sign-in link */}
        {onSignIn && (
          <div style={{ textAlign: "center", marginTop: 20 }}>
            <span style={{ fontSize: 13, color: "#64748b", fontWeight: 600 }}>Already have an account? </span>
            <button
              onClick={onSignIn}
              style={{
                background: "none", border: "none", padding: 0,
                fontSize: 13, fontWeight: 900, color: "#0d9488",
                cursor: "pointer", textDecoration: "underline",
              }}
            >
              Sign in
            </button>
          </div>
        )}

      </div>

      {/* ── LEGAL FOOTER ── */}
      <div style={{
        padding: "16px 20px 32px",
        background: "#f8fafc",
        borderTop: "1px solid #e2e8f0",
        textAlign: "center",
      }}>
        <div style={{ display: "flex", justifyContent: "center", gap: 20, flexWrap: "wrap" }}>
          <a
            href="https://moneymission.app/privacy"
            target="_blank"
            rel="noopener noreferrer"
            style={{ fontSize: 11, fontWeight: 700, color: "#64748b", textDecoration: "none" }}
          >
            Privacy Policy
          </a>
          <span style={{ fontSize: 11, color: "#cbd5e1" }}>·</span>
          <a
            href="https://moneymission.app/terms"
            target="_blank"
            rel="noopener noreferrer"
            style={{ fontSize: 11, fontWeight: 700, color: "#64748b", textDecoration: "none" }}
          >
            Terms of Service
          </a>
        </div>
        <div style={{ marginTop: 8, fontSize: 10, color: "#94a3b8", fontWeight: 600 }}>
          © {new Date().getFullYear()} MoneyMission. All rights reserved.
        </div>
      </div>

    </div>
  );
}
